<template>
  <b-row>
    <b-col>
      <b-row>
        <b-col class="text-left">
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="flat-primary"
            class="my-1"
            @click="showFilters = !showFilters"
          >
            <FeatherIcon icon="FilterIcon" />
            Filters
          </b-button>
        </b-col>
        <b-col
          v-if="canCreate"
          class="text-right"
        >
          <b-button
            variant="primary"
            class="my-1"
            :to="{name:'add-shipment'}"
          >
            Add shipment
          </b-button>
        </b-col>
      </b-row>
      <b-card
        v-show="showFilters"
        title="Filters"
      >
        <b-form @keyup.enter="filterData">
          <b-row>
            <b-col>
              <div class="d-flex justify-content-between">
                <b-form-group
                  label="Document number"
                  label-for="document-number"
                  class="full-width"
                >
                  <b-input-group>
                    <b-form-tags
                      id="document-number"
                      v-model="filters.document_number"
                      label="Document number"
                      :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
                      separator=" ,;"
                      placeholder="Enter document numbers separated by space, comma or semicolon"
                      no-add-on-enter
                      add-on-change
                      clearable
                    />
                    <b-input-group-append v-if="filters.document_number.length">
                      <b-button
                        id="filter-reset"
                        variant="secondary"
                        size="sm"
                        @click="filters.document_number = []"
                      >
                        <feather-icon icon="XIcon" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group
                label="Parcel tracking number"
                label-for="parcel-tracking-number"
              >
                <b-form-input
                  id="parcel-tracking-number"
                  v-model="filters.tracking_number"
                  name="parcel-tracking-number"
                  autocomplete="off"
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Container number"
                label-for="container-number"
              >
                <b-form-input
                  id="container-number"
                  v-model="filters.container_code"
                  name="container-number"
                  autocomplete="off"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group
                label="Date range"
                label-for="date-range"
              >
                <flat-pickr
                  id="date-range"
                  v-model="filters.dateRange"
                  class="form-control"
                  :config="{ mode: 'range' }"
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="Status"
                label-for="status"
              >
                <v-select
                  v-model="selectedFilterStatus"
                  :options="statusOptions"
                  :clearable="false"
                  input-id="status"
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group
                label="MRN"
                label-for="mrn"
              >
                <b-form-input
                  id="mrn"
                  v-model="filters.mrn"
                  name="mrn"
                  autocomplete="off"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="text-right">
              <b-button
                type="button"
                variant="secondary"
                @click="clearFilterData()"
              >
                Clear
              </b-button>
              <b-button
                class="ml-1"
                type="button"
                variant="primary"
                @click="filterData()"
              >
                Filter
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-card>
      <shipments-table
        :table-data="data.shipments"
        :statuses="statusOptions"
        :show-logo-column="userCompaniesCount > 1 || userHasAuthorisedCompanies"
      />
    </b-col>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BButton, BFormTags, BInputGroupAppend, BInputGroup,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import Vue from 'vue'
import { EventBus } from '@core/utils/utils'
import vSelect from 'vue-select'
import ShipmentsTable from '@/views/shipments/ShipmentsTable.vue'
import { parseFilters } from '@core/utils/filter'
import { buildQueryFromFilters, pushQueryIfChanged, restoreFiltersFromQuery } from '@core/utils/filterQuerySync'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BInputGroup,
    BInputGroupAppend,
    BFormTags,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    flatPickr,
    BFormInput,
    ShipmentsTable,
    BCard,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      showFilters: true,
      statusOptions: [
        { label: 'N/A', value: '', variant: '' },
        { label: 'Created', value: 'created', variant: 'info' },
        { label: 'In progress', value: 'in-progress', variant: 'info' },
        { label: 'Done', value: 'done', variant: 'light-success' },
        { label: 'Parsed', value: 'parsed', variant: 'success' },
        { label: 'Archived', value: 'truncated', variant: 'secondary' },
        { label: 'Attention', value: 'attention', variant: 'warning' },
      ],
      filters: {
        document_number: [],
        container_code: '',
        tracking_number: '',
        mrn: '',
        dateRange: '',
        status: '',
      },
      clean_filters: {},
      selectedFilterStatus: null,
      data: {
        shipments: [],
      },
    }
  },
  computed: {
    canCreate() {
      return this.$permissions().hasPermission('create shipment')
        || this.$permissions().hasPermission('add manifest')
    },
    userCompaniesCount() {
      return Vue.prototype.$profile().data.user.companies.length
    },
    userHasAuthorisedCompanies() {
      return this.$profile().data.user.authorisations.length
    },
  },
  created() {
    EventBus.$on('companyListUpdated', this.companyListUpdated)
  },
  beforeDestroy() {
    EventBus.$off('companyListUpdated', this.companyListUpdated)
  },
  mounted() {
    this.storeCleanFilters()
    this.restoreFiltersFromQuery()
  },
  methods: {
    companyListUpdated() {
      // TODO: company authorisations!
      this.filterData()
    },
    storeCleanFilters() {
      this.clean_filters = { ...this.filters }
    },
    restoreFiltersFromQuery() {
      const restored = restoreFiltersFromQuery(this.$route.query, this.filters, {
        document_number: 'array',
        tracking_number: 'string',
        container_code: 'string',
        mrn: 'string',
        dateRange: 'string',
        status: 'string',
      })
      if (restored && this.filters.status) {
        this.selectedFilterStatus = this.statusOptions.find(s => s.value === this.filters.status) || null
      }
      this.filterData()
    },
    filterData() {
      if (this.selectedFilterStatus) {
        this.filters.status = this.selectedFilterStatus.value
        this.page = 1
      }
      pushQueryIfChanged(this.$router, this.$route, buildQueryFromFilters(this.filters, ['document_number']))
      const filters = { ...this.filters }
      filters.document_number = this.filters.document_number?.join(',') || []
      EventBus.$emit('updateFilters', parseFilters(filters))
    },
    clearFilterData() {
      this.selectedFilterStatus = null
      Object.assign(this.filters, this.clean_filters)
      this.filterData()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
